import { useMemo } from 'react';
import { getTeamQuota } from '@/app/apis';
import { NEW_MEMBERSHIP_REFERENCE_LIMIT, ONE_MINUTE, REFERENCE_RIGHT_LIST } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { SubsPlanType } from '../types';
import { useFetch } from './use-fetch';
import { useMembershipRevision } from './use-membership-revision';

const DEFAULT_NOPEAK_DAILY_LIMIT = 200;
const DEFAULT_TEAM_NOPEAK_DAILY_LIMIT = 500;

/**
 * 获取用户配额信息
 * - team 用户：从 API 获取团队配置的配额
 * - 非 team 用户：根据订阅计划返回对应的默认配额
 */
export const useQuota = (): {
  /** 主体每月限额 */
  elementMonthlyLimit: number;
  /** 错峰每日限额 */
  nopeakDailyLimit: number;
} => {
  const subsPlan = useStore((state) => (state.userInfo?.subs_plan || 'free') as SubsPlanType);
  const isTeamSpace = useStore((state) => state.isTeamSpace);
  const isNewMembership = useMembershipRevision();

  // 只有 team 用户才请求 API
  const { data } = useFetch(getTeamQuota, {
    ready: isTeamSpace,
    cacheKey: 'teamQuota',
    staleTime: ONE_MINUTE,
  });

  // 计算配额信息
  const quota = useMemo(() => {
    if (!isTeamSpace) {
      return {
        elementMonthlyLimit:
          isNewMembership && subsPlan !== 'free' && subsPlan !== 'team_v2'
            ? NEW_MEMBERSHIP_REFERENCE_LIMIT
            : REFERENCE_RIGHT_LIST[subsPlan],
        nopeakDailyLimit: DEFAULT_NOPEAK_DAILY_LIMIT,
      };
    }

    const { element_monthly_limit: elementMonthlyLimit = REFERENCE_RIGHT_LIST.team_v2, nopeak_quota } =
      data?.data?.quota ?? {};
    const nopeakDailyLimit = nopeak_quota?.total ?? DEFAULT_TEAM_NOPEAK_DAILY_LIMIT;

    return { elementMonthlyLimit, nopeakDailyLimit };
  }, [isTeamSpace, subsPlan, data, isNewMembership]);

  return quota;
};
